import React, { useState, useEffect } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { StatusApi } from "@/lib/apiClient";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Trash2, Phone, PhoneCall, Mail, Globe, MapPin, Search, ExternalLink, Store, History, Loader2, ChevronUp, ChevronDown, ChevronsUpDown, Plus, Pencil, Check, X } from "lucide-react";

const STATUS_STYLES = {
  "da fare": "bg-red-100 text-red-700 border-red-200",
  chiamato: "bg-amber-100 text-amber-700 border-amber-200",
  lavorato: "bg-emerald-100 text-emerald-700 border-emerald-200",
  "non interessato": "bg-gray-100 text-gray-600 border-gray-300",
  "da richiamare": "bg-orange-100 text-orange-700 border-orange-200",
  "appuntamento fissato": "bg-violet-100 text-violet-700 border-violet-200",
  acquisito: "bg-sky-100 text-sky-700 border-sky-200",
  concluso: "bg-teal-100 text-teal-700 border-teal-200",
  "numero errato/inesistente": "bg-rose-100 text-rose-700 border-rose-200",
};

const STATUS_OPTIONS = ["da fare", "da richiamare", "appuntamento fissato", "non interessato", "acquisito", "concluso", "numero errato/inesistente"];

function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return "—";
  }
}

function fmtDateTime(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
}

const ACT_LABEL = { call: "Chiamata", email: "Email inviata" };

// Tooltip CSS istantaneo (no ritardo del title nativo)
function Tip({ label, children }) {
  if (!label) return children;
  return (
    <span className="relative group/tip inline-flex">
      {children}
      <span className="pointer-events-none absolute z-50 left-1/2 -translate-x-1/2 bottom-full mb-1 whitespace-nowrap rounded bg-neutral-900 text-white text-[11px] px-2 py-1 opacity-0 group-hover/tip:opacity-100 transition-opacity duration-75">
        {label}
      </span>
    </span>
  );
}

// Rimuove comune e provincia (es. "20864 Agrate Brianza (MB)") dall'indirizzo mostrato
function cleanAddress(addr, comune) {
  if (!addr) return "";
  let s = String(addr);
  s = s.replace(/\(\s*[A-Za-z]{2}\s*\)/g, "");        // provincia (MB)
  s = s.replace(/\b\d{5}\b/g, "");                     // CAP
  if (comune) {
    const c = comune.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    s = s.replace(new RegExp(c, "ig"), "");
  }
  s = s.replace(/\s{2,}/g, " ").replace(/\s*[,\-–]\s*$/g, "").replace(/^\s*[,\-–]\s*/g, "").trim();
  return s;
}

// Intestazione colonna ordinabile
function SortHeader({ label, sortKey, sort, onSort, className = "" }) {
  const active = sort.key === sortKey;
  return (
    <TableHead className={`font-display text-xs uppercase tracking-wide ${className}`}>
      <button type="button" onClick={() => onSort(sortKey)} data-testid={`sort-${sortKey}`}
        className={`inline-flex items-center gap-1 hover:text-primary transition-colors ${active ? "text-primary" : ""}`}>
        {label}
        {active ? (sort.dir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)
                : <ChevronsUpDown className="h-3 w-3 opacity-40" />}
      </button>
    </TableHead>
  );
}

// Data di richiamo editabile; evidenzia in rosso se scaduta, in arancio se oggi.
function RecallCell({ value, onSave, testid }) {
  const [val, setVal] = useState(value || "");
  useEffect(() => setVal(value || ""), [value]);
  const today = new Date().toISOString().slice(0, 10);
  const overdue = val && val < today;
  const isToday = val && val === today;
  return (
    <div className="flex items-center gap-1">
      <input
        type="date"
        data-testid={testid}
        value={val || ""}
        onChange={(e) => { setVal(e.target.value); if (e.target.value !== (value || "")) onSave(e.target.value); }}
        className={`h-8 text-xs rounded px-1.5 border bg-transparent outline-none w-[120px] ${
          overdue ? "border-red-300 bg-red-50 text-red-700 font-semibold"
          : isToday ? "border-orange-300 bg-orange-50 text-orange-700 font-semibold"
          : "border-transparent hover:border-border text-muted-foreground"}`}
      />
      {val && (
        <button onClick={() => { setVal(""); onSave(""); }} data-testid={`${testid}-clear`}
          title="Rimuovi richiamo" className="text-muted-foreground/50 hover:text-red-500 text-xs">✕</button>
      )}
    </div>
  );
}

// Scheda azienda: dati + storico completo delle attività svolte (con modifica/eliminazione).
function LeadDetailDialog({ lead, onClose, onActivityChanged }) {
  const [acts, setActs] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = () => {
    if (!lead) return;
    StatusApi.leadActivities(lead.id).then(setActs).catch(() => setActs([]));
  };
  useEffect(() => {
    if (!lead) return;
    setActs(null); setEditId(null); setConfirmDel(null);
    StatusApi.leadActivities(lead.id).then(setActs).catch(() => setActs([]));
  }, [lead]);

  const saveEdit = async (a) => {
    setBusy(true);
    try {
      await StatusApi.updateActivity(a.id, editVal.trim());
      setEditId(null);
      reload();
      toast.success("Attività aggiornata");
    } catch { toast.error("Errore aggiornamento attività"); }
    finally { setBusy(false); }
  };

  const doDelete = async (a) => {
    setBusy(true);
    try {
      const r = await StatusApi.deleteActivity(a.id);
      setConfirmDel(null);
      reload();
      if (r && r.lead_id) {
        onActivityChanged && onActivityChanged(r.lead_id, {
          last_activity_at: r.last_activity_at || "",
          last_activity_by: r.last_activity_by || "",
          last_activity_type: r.last_activity_type || "",
        });
      }
      toast.success("Attività eliminata");
    } catch { toast.error("Errore eliminazione attività"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="lead-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <History className="h-5 w-5 text-primary" /> {lead?.company_name}
          </DialogTitle>
        </DialogHeader>
        {lead && (
          <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 shrink-0">
            {lead.address && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{lead.address}</span>}
            {lead.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{lead.phone}</span>}
            {lead.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{lead.email}</span>}
            {lead.reference_person && <span>Referente: {lead.reference_person}</span>}
          </div>
        )}
        <div className="overflow-auto flex-1 mt-2 space-y-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Attività svolte</div>
            {acts === null ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
                <Loader2 className="h-4 w-4 animate-spin" /> Caricamento...
              </div>
            ) : acts.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center" data-testid="lead-activities-empty">
                Nessuna attività registrata per questa azienda.
              </div>
            ) : (
              <ul className="space-y-2" data-testid="lead-activities-list">
                {acts.map((a) => (
                  <li key={a.id} data-testid={`activity-item-${a.id}`} className="border border-border rounded-md p-2.5 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold flex items-center gap-1.5">
                        {a.type === "call" ? <PhoneCall className="h-3.5 w-3.5 text-emerald-600" /> : <Mail className="h-3.5 w-3.5 text-blue-600" />}
                        {ACT_LABEL[a.type] || a.type}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-muted-foreground">{fmtDateTime(a.at)}</span>
                        {confirmDel !== a.id && editId !== a.id && (
                          <>
                            <Tip label="Modifica esito">
                              <button data-testid={`activity-edit-${a.id}`} onClick={() => { setEditId(a.id); setEditVal(a.outcome || ""); }}
                                className="text-muted-foreground hover:text-primary transition-colors">
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                            </Tip>
                            <Tip label="Elimina attività">
                              <button data-testid={`activity-delete-${a.id}`} onClick={() => setConfirmDel(a.id)}
                                className="text-muted-foreground hover:text-red-500 transition-colors">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </Tip>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Operatore: <b className="text-foreground">{a.user_name || "—"}</b>
                      {editId !== a.id && a.outcome ? <> · Esito: {a.outcome}</> : null}
                    </div>
                    {editId === a.id && (
                      <div className="mt-2 space-y-2">
                        <Textarea data-testid={`activity-edit-input-${a.id}`} value={editVal} onChange={(e) => setEditVal(e.target.value)}
                          placeholder="Esito / note..." className="text-xs min-h-[56px]" />
                        <div className="flex items-center gap-2">
                          <Button size="sm" className="h-7" disabled={busy} data-testid={`activity-edit-save-${a.id}`} onClick={() => saveEdit(a)}>
                            <Check className="h-3.5 w-3.5 mr-1" /> Salva
                          </Button>
                          <Button size="sm" variant="outline" className="h-7" data-testid={`activity-edit-cancel-${a.id}`} onClick={() => setEditId(null)}>
                            <X className="h-3.5 w-3.5 mr-1" /> Annulla
                          </Button>
                        </div>
                      </div>
                    )}
                    {confirmDel === a.id && (
                      <div className="mt-2 flex items-center gap-2 bg-red-50 border border-red-200 rounded p-2">
                        <span className="text-xs text-red-700 flex-1">Eliminare questa attività? Aggiornerà l'ultimo contatto.</span>
                        <Button size="sm" className="h-7 bg-red-600 hover:bg-red-700 text-white" disabled={busy} data-testid={`activity-delete-confirm-${a.id}`} onClick={() => doDelete(a)}>
                          Elimina
                        </Button>
                        <Button size="sm" variant="outline" className="h-7" data-testid={`activity-delete-cancel-${a.id}`} onClick={() => setConfirmDel(null)}>
                          Annulla
                        </Button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {lead?.notes && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Note</div>
              <pre className="text-xs whitespace-pre-wrap bg-muted/50 rounded p-2.5 font-sans" data-testid="lead-detail-notes">{lead.notes}</pre>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ClosedBadge({ status, closed }) {
  if (closed || status === "CLOSED_PERMANENTLY")
    return <span data-testid="closed-badge" className="text-[10px] font-bold text-red-600 border border-red-200 bg-red-50 rounded px-1 py-0.5">CESSATA</span>;
  if (status === "CLOSED_TEMPORARILY")
    return <span data-testid="closed-badge" className="text-[10px] font-bold text-amber-600 border border-amber-200 bg-amber-50 rounded px-1 py-0.5">CHIUSA TEMP.</span>;
  return null;
}

function EmailInvalidBadge({ invalid }) {
  if (!invalid) return null;
  return <span data-testid="email-invalid-badge" className="text-[10px] font-bold text-red-600 border border-red-200 bg-red-50 rounded px-1 py-0.5">EMAIL NON VALIDA</span>;
}

function EditableCell({ value, onSave, placeholder, testid }) {
  const [val, setVal] = useState(value || "");
  useEffect(() => setVal(value || ""), [value]);
  return (
    <Input
      data-testid={testid}
      value={val}
      placeholder={placeholder}
      title={val || placeholder}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => { if ((val || "") !== (value || "")) onSave(val); }}
      className="h-8 text-xs border-transparent hover:border-border focus:border-primary bg-transparent px-2"
    />
  );
}

// Modifica completa dei contatti di un'azienda (tutti i campi in un'unica scheda).
function LeadEditDialog({ lead, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (lead) setForm({
      company_name: lead.company_name || "", reference_person: lead.reference_person || "",
      category: lead.category || "", address: lead.address || "", phone: lead.phone || "",
      email: lead.email || "", website: lead.website || "", notes: lead.notes || "",
    });
  }, [lead]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.company_name.trim()) { toast.error("Il nome azienda è obbligatorio"); return; }
    setSaving(true);
    try {
      await StatusApi.updateLead(lead.id, form);
      onSaved && onSaved(lead.id, form);
      toast.success("Contatti salvati");
      onClose();
    } catch { toast.error("Errore salvataggio"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg" data-testid="lead-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Pencil className="h-5 w-5 text-primary" /> Modifica contatti azienda
          </DialogTitle>
        </DialogHeader>
        {form && (
          <div className="space-y-3 max-h-[70vh] overflow-auto pr-1">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-muted-foreground">Nome azienda</label>
              <Input data-testid="edit-company_name" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className="h-9" autoFocus />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-muted-foreground">Referente</label>
              <Input data-testid="edit-reference_person" value={form.reference_person} onChange={(e) => set("reference_person", e.target.value)} className="h-9" placeholder="Persona di riferimento" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-muted-foreground">Categoria</label>
              <Input data-testid="edit-category" value={form.category} onChange={(e) => set("category", e.target.value)} className="h-9" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-muted-foreground">Indirizzo</label>
              <Input data-testid="edit-address" value={form.address} onChange={(e) => set("address", e.target.value)} className="h-9" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-muted-foreground">Telefono</label>
                <Input data-testid="edit-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="h-9" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-muted-foreground">Email</label>
                <Input data-testid="edit-email" value={form.email} onChange={(e) => set("email", e.target.value)} className="h-9" />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-muted-foreground">Sito web</label>
              <Input data-testid="edit-website" value={form.website} onChange={(e) => set("website", e.target.value)} className="h-9" placeholder="https://..." />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-muted-foreground">Note</label>
              <Textarea data-testid="edit-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="min-h-[70px] text-sm" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={onClose} data-testid="edit-cancel">Annulla</Button>
              <Button onClick={save} disabled={saving} data-testid="edit-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const CAMP_BADGE = {
  inviata: ["Inviata", "bg-slate-100 text-slate-600 border-slate-200"],
  aperta: ["Aperta", "bg-blue-100 text-blue-700 border-blue-200"],
  cliccata: ["Cliccata", "bg-emerald-100 text-emerald-700 border-emerald-200"],
  richiesta: ["Richiesta", "bg-violet-100 text-violet-700 border-violet-200"],
  cancellato: ["Cancellato", "bg-red-100 text-red-700 border-red-200"],
};
function CampaignBadge({ status, unsub }) {
  if (unsub) return <span className="text-[10px] px-1.5 py-0.5 rounded border bg-red-100 text-red-700 border-red-200" data-testid="camp-badge">Cancellato</span>;
  const b = CAMP_BADGE[status];
  if (!b) return null;
  return <span className={`text-[10px] px-1.5 py-0.5 rounded border ${b[1]}`} data-testid="camp-badge">{b[0]}</span>;
}

// Row highlight by campaign engagement so responsive companies stand out.
const ROW_HL = {
  richiesta: "bg-violet-50 border-l-4 border-l-violet-400",
  cliccata: "bg-emerald-50 border-l-4 border-l-emerald-400",
  aperta: "bg-blue-50/70 border-l-4 border-l-blue-300",
  cancellato: "bg-red-50 border-l-4 border-l-red-300",
};
function rowHighlight(l) {
  if (l.email_invalid) return "bg-red-50 border-l-4 border-l-red-400";
  if (l.closed || l.business_status === "CLOSED_PERMANENTLY") return "bg-gray-100 border-l-4 border-l-gray-400 opacity-70";
  if (l.unsubscribed) return ROW_HL.cancellato;
  return ROW_HL[l.campaign_status] || "";
}

// Referente shown small under the company name (editable, paste-friendly).
function RefLine({ value, onSave, testid }) {  const [val, setVal] = useState(value || "");
  useEffect(() => setVal(value || ""), [value]);
  return (
    <input
      data-testid={testid}
      value={val}
      placeholder="+ referente"
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => { if ((val || "") !== (value || "")) onSave(val); }}
      className="mt-0.5 w-full max-w-[220px] bg-transparent text-[11px] text-muted-foreground placeholder:text-muted-foreground/50 border border-transparent hover:border-border focus:border-primary rounded px-1 py-0.5 outline-none"
    />
  );
}

// Tap-to-reveal contact icon (used on mobile): green if present, red if absent.
// Phone/email are editable (paste-friendly); web opens the site (or Google as fallback).
function ContactBadge({ type, value, testid, onSave, searchQuery }) {
  const Icon = type === "phone" ? Phone : type === "web" ? Globe : type === "places" ? MapPin : Mail;
  const present = !!(value && value.trim());
  const label = type === "phone" ? "Telefono" : type === "web" ? "Sito web" : type === "places" ? "Indirizzo" : "Email";
  const [val, setVal] = useState(value || "");
  useEffect(() => setVal(value || ""), [value]);

  let contactHref = value;
  if (type === "phone") contactHref = `tel:${value}`;
  else if (type === "email") contactHref = `mailto:${value}`;
  else if (type === "web" && value && !value.startsWith("http")) contactHref = `https://${value}`;
  const googleHref = `https://www.google.com/search?q=${encodeURIComponent(searchQuery || "")}`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Tip label={present ? value : `${label} mancante`}>
          <button
            data-testid={testid}
            className={`p-1.5 rounded-full border transition-colors ${
              present
                ? "text-emerald-600 border-emerald-200 bg-emerald-50 hover:bg-emerald-100"
                : "text-red-500 border-red-200 bg-red-50 hover:bg-red-100"
            }`}
            aria-label={label}
          >
            <Icon className="h-4 w-4" />
          </button>
        </Tip>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-3 bg-white space-y-2">
        {type === "web" ? (
          present ? (
            <a href={contactHref} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-value`}
               className="inline-flex items-center gap-1.5 text-primary text-sm font-medium break-all hover:underline">
              <ExternalLink className="h-3.5 w-3.5 shrink-0" /> Apri sito web
            </a>
          ) : (
            <a href={googleHref} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-google`}
               className="inline-flex items-center gap-1.5 text-primary text-sm font-medium hover:underline">
              <Search className="h-3.5 w-3.5 shrink-0" /> Cerca su Google
            </a>
          )
        ) : (
          <>
            {present && type === "places" && (
              <div data-testid={`${testid}-value`} className="text-sm text-foreground break-words">{value}</div>
            )}
            {present && type !== "places" && (
              <a href={contactHref} data-testid={`${testid}-value`}
                 className="block text-primary text-sm font-medium break-all hover:underline">
                {value}
              </a>
            )}
            <Input
              data-testid={`${testid}-input`}
              value={val}
              onChange={(e) => setVal(e.target.value)}
              onBlur={() => { if ((val || "") !== (value || "")) onSave && onSave(val); }}
              placeholder={type === "phone" ? "Incolla telefono..." : type === "places" ? "Incolla indirizzo..." : "Incolla email..."}
              className="h-8 text-xs"
            />
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

// Nuovo contatto: registra un contatto (conta nelle statistiche) e OBBLIGA a indicare il nuovo stato.
function NewContactButton({ lead, onLogged }) {
  const [open, setOpen] = useState(false);
  const [outcome, setOutcome] = useState("");
  const [status, setStatus] = useState("");
  const [recall, setRecall] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const r = await StatusApi.logCall(lead.id, outcome.trim(), status);
      const extra = {};
      if (status === "da richiamare" && recall) {
        await StatusApi.updateLead(lead.id, { recall_date: recall });
        extra.recall_date = recall;
      } else if ((r.status || "") !== "da richiamare") {
        extra.recall_date = "";
      }
      onLogged && onLogged(lead.id, { notes: r.notes, status: r.status,
        last_activity_at: r.last_activity_at, last_activity_by: r.last_activity_by, last_activity_type: r.last_activity_type, ...extra });
      toast.success("Contatto registrato");
      setOutcome(""); setStatus(""); setRecall(""); setOpen(false);
    } catch {
      toast.error("Errore nella registrazione");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setStatus(""); }}>
      <PopoverTrigger asChild>
        <button data-testid={`new-contact-${lead.id}`} title="Nuovo contatto (registra e aggiorna lo stato)"
          className="flex items-center justify-center h-6 w-6 rounded-full bg-primary text-primary-foreground hover:brightness-110 transition-all">
          <Plus className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-3 bg-white space-y-2.5" data-testid={`contact-popover-${lead.id}`}>
        <div className="text-xs font-semibold text-foreground">Nuovo contatto</div>
        <p className="text-[11px] text-muted-foreground">Registra la chiamata (conta nelle statistiche). Cambiare lo stato è facoltativo.</p>
        <Textarea data-testid={`contact-outcome-${lead.id}`} value={outcome} onChange={(e) => setOutcome(e.target.value)}
          placeholder="Esito / note (opzionale): es. interessato, richiamare lunedì..." className="text-xs min-h-[60px]" />
        <div>
          <label className="text-[11px] font-semibold text-muted-foreground">Nuovo stato (facoltativo)</label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-8 text-xs mt-1" data-testid={`contact-status-${lead.id}`}>
              <SelectValue placeholder="Seleziona lo stato…" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {status === "da richiamare" && (
          <div>
            <label className="text-[11px] font-semibold text-muted-foreground">Data richiamo</label>
            <input type="date" data-testid={`contact-recall-${lead.id}`} value={recall}
              onChange={(e) => setRecall(e.target.value)}
              className="h-8 text-xs rounded px-2 border border-border w-full mt-1" />
          </div>
        )}
        <Button size="sm" className="h-8 w-full" disabled={saving} data-testid={`contact-save-${lead.id}`} onClick={submit}>
          Registra chiamata
        </Button>
      </PopoverContent>
    </Popover>
  );
}

export default function LeadsTable({ leads, onChanged, selectedIds = new Set(), onToggle = () => {}, onToggleAll = () => {} }) {
  const patch = async (id, data, opts = {}) => {
    const prev = leads.find((x) => x.id === id) || {};
    const undoData = {};
    Object.keys(data).forEach((k) => { undoData[k] = prev[k] ?? ""; });
    try {
      await StatusApi.updateLead(id, data);
      onChanged && onChanged(id, data);
      if (opts.silent) return;
      toast.success("Modifica salvata", {
        duration: 6000,
        action: {
          label: "Annulla",
          onClick: async () => {
            try {
              await StatusApi.updateLead(id, undoData);
              onChanged && onChanged(id, undoData);
              toast.info("Modifica annullata");
            } catch { toast.error("Impossibile annullare"); }
          },
        },
      });
    } catch {
      toast.error("Errore aggiornamento");
    }
  };

  const remove = async (id) => {
    try {
      await StatusApi.deleteLead(id);
      toast.success("Lead eliminato");
      onChanged && onChanged();
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  const [enriching, setEnriching] = useState(null);
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [detailLead, setDetailLead] = useState(null);
  const [editLead, setEditLead] = useState(null);
  const [sort, setSort] = useState({ key: "company_name", dir: "asc" });

  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const sortedLeads = React.useMemo(() => {
    if (!sort.key) return leads;
    const val = (l) => {
      const v = l[sort.key];
      if (sort.key === "last_seen_at") return l.last_seen_at || l.created_at || "";
      return v == null ? "" : v;
    };
    const arr = [...leads].sort((a, b) => {
      const av = val(a), bv = val(b);
      const ae = av === "" || av == null, be = bv === "" || bv == null;
      if (ae && be) return 0;
      if (ae) return 1;   // vuoti sempre in fondo
      if (be) return -1;
      return String(av).localeCompare(String(bv), "it", { numeric: true, sensitivity: "base" });
    });
    return sort.dir === "desc" ? arr.reverse() : arr;
  }, [leads, sort]);

  const enrich = async (l) => {
    setEnriching(l.id);
    try {
      const updated = await StatusApi.enrichLead(l.id);
      if ((updated.email && !l.email) || (updated.phone && !l.phone)) {
        toast.success("Contatti trovati!");
      } else {
        toast.info("Nessun nuovo contatto trovato sul sito");
      }
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore ricerca contatti");
    } finally {
      setEnriching(null);
    }
  };

  if (!leads.length) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="leads-empty">
        <MapPin className="h-10 w-10 text-muted-foreground/40 mb-3" />
        <p className="font-display font-bold text-lg">Nessun nominativo</p>
        <p className="text-sm text-muted-foreground max-w-sm mt-1">
          Avvia una raccolta per questo comune usando il pulsante "Avvia raccolta" in alto.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-border rounded-md overflow-hidden bg-white">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/60 hover:bg-muted/60">
            <TableHead className="w-8">
              <Checkbox
                data-testid="select-all-leads"
                checked={leads.length > 0 && leads.every((l) => selectedIds.has(l.id))}
                onCheckedChange={(v) => onToggleAll(leads.map((l) => l.id), !!v)}
                aria-label="Seleziona tutti" />
            </TableHead>
            <SortHeader label="Azienda" sortKey="company_name" sort={sort} onSort={toggleSort} />
            <SortHeader label="Categoria" sortKey="category" sort={sort} onSort={toggleSort} />
            {/* Mobile: solo icone contatti */}
            <TableHead className="font-display text-xs uppercase tracking-wide md:hidden" title="Contatti (telefono, email, sito)">Contatti</TableHead>
            <SortHeader label="Indirizzo" sortKey="address" sort={sort} onSort={toggleSort} className="hidden md:table-cell" />
            <TableHead className="font-display text-xs uppercase tracking-wide w-12 text-center hidden md:table-cell" title="Telefono — passa il mouse per vederlo">T</TableHead>
            <TableHead className="font-display text-xs uppercase tracking-wide w-12 text-center hidden md:table-cell" title="Email — passa il mouse per vederla">E</TableHead>
            <TableHead className="font-display text-xs uppercase tracking-wide w-12 text-center hidden md:table-cell" title="Sito web — passa il mouse per vederlo">W</TableHead>
            <SortHeader label="Ultimo contatto" sortKey="last_activity_at" sort={sort} onSort={toggleSort} className="w-40 hidden md:table-cell" />
            <SortHeader label="Stato" sortKey="status" sort={sort} onSort={toggleSort} className="w-36" />
            <SortHeader label="Da richiamare" sortKey="recall_date" sort={sort} onSort={toggleSort} className="w-32 hidden md:table-cell" />
            <TableHead className="font-display text-xs uppercase tracking-wide w-48 hidden md:table-cell" title="Note e storico contatti">Note</TableHead>
            <TableHead className="w-10" title="Azioni: registra chiamata, scheda, sito, elimina"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedLeads.map((l) => (
            <TableRow key={l.id} data-testid={`lead-row-${l.id}`}
              className={`hover:bg-muted/40 transition-colors duration-200 ${rowHighlight(l)}`}>
              <TableCell className="py-2 w-8">
                <Checkbox checked={selectedIds.has(l.id)} onCheckedChange={() => onToggle(l.id)}
                  data-testid={`select-lead-${l.id}`} aria-label="Seleziona" />
              </TableCell>
              <TableCell className="py-2">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-semibold text-sm">{l.company_name}</span>
                  <ClosedBadge status={l.business_status} closed={l.closed} />
                  <EmailInvalidBadge invalid={l.email_invalid} />
                  <CampaignBadge status={l.campaign_status} unsub={l.unsubscribed} />
                </div>
                <RefLine value={l.reference_person} testid={`lead-referente-${l.id}`}
                  onSave={(v) => patch(l.id, { reference_person: v })} />
              </TableCell>
              <TableCell className="py-2 min-w-[170px]">
                <EditableCell value={l.category} placeholder="Categoria..."
                  testid={`lead-category-${l.id}`}
                  onSave={(v) => patch(l.id, { category: v })} />
              </TableCell>
              {/* Mobile: icone contatti (verde=presente, rosso=assente), tap per vedere/incollare */}
              <TableCell className="py-2 md:hidden">
                <div className="flex items-center gap-1.5">
                  <ContactBadge type="places" value={l.address} testid={`lead-address-badge-${l.id}`}
                    onSave={(v) => patch(l.id, { address: v })} />
                  <ContactBadge type="phone" value={l.phone} testid={`lead-phone-badge-${l.id}`}
                    onSave={(v) => patch(l.id, { phone: v })} />
                  <ContactBadge type="email" value={l.email} testid={`lead-email-badge-${l.id}`}
                    onSave={(v) => patch(l.id, { email: v })} />
                  <ContactBadge type="web" value={l.website} testid={`lead-web-badge-${l.id}`}
                    searchQuery={`${l.company_name} ${l.comune || ""}`} />
                </div>
              </TableCell>
              <TableCell className="py-2 text-xs text-muted-foreground max-w-[220px] hidden md:table-cell" title={cleanAddress(l.address, l.comune)}>{cleanAddress(l.address, l.comune)}</TableCell>
              <TableCell className="py-1 text-xs hidden md:table-cell text-center w-12">
                <ContactBadge type="phone" value={l.phone} testid={`lead-phone-${l.id}`}
                  onSave={(v) => patch(l.id, { phone: v })} />
              </TableCell>
              <TableCell className="py-1 text-xs hidden md:table-cell text-center w-12">
                <ContactBadge type="email" value={l.email} testid={`lead-email-${l.id}`}
                  onSave={(v) => patch(l.id, { email: v })} />
              </TableCell>
              <TableCell className="py-1 text-xs hidden md:table-cell text-center w-12">
                {l.website ? (
                  <Tip label={l.website}>
                    <a href={l.website.startsWith("http") ? l.website : `https://${l.website}`}
                       target="_blank" rel="noopener noreferrer" data-testid={`lead-web-${l.id}`}
                       className="inline-flex p-1.5 rounded-full border text-emerald-600 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors">
                      <Globe className="h-3.5 w-3.5" />
                    </a>
                  </Tip>
                ) : (
                  <span data-testid={`lead-web-${l.id}`} className="inline-flex p-1.5 rounded-full border text-red-400 border-red-200 bg-red-50" title="Sito mancante">
                    <Globe className="h-3.5 w-3.5" />
                  </span>
                )}
              </TableCell>
              <TableCell className="py-2 hidden md:table-cell">
                <button data-testid={`lead-lastcontact-${l.id}`} onClick={() => setDetailLead(l)}
                  className="text-left group w-full" title="Apri scheda azienda e attività">
                  {l.last_activity_at ? (
                    <>
                      <span className="text-xs text-foreground group-hover:text-primary flex items-center gap-1">
                        {l.last_activity_type === "email" ? <Mail className="h-3 w-3" /> : <PhoneCall className="h-3 w-3 text-emerald-600" />}
                        {fmtDateTime(l.last_activity_at)}
                      </span>
                      <span className="text-[11px] text-muted-foreground block">{l.last_activity_by || ""}</span>
                    </>
                  ) : (
                    <span className="text-xs text-muted-foreground/60 group-hover:text-primary inline-flex items-center gap-1">
                      <History className="h-3 w-3" /> nessun contatto
                    </span>
                  )}
                </button>
              </TableCell>
              <TableCell className="py-2">
                <Select value={l.status} onValueChange={(v) => { if (v !== l.status) patch(l.id, v !== "da richiamare" ? { status: v, recall_date: "" } : { status: v }, { silent: true }); }}>
                  <SelectTrigger data-testid={`lead-status-${l.id}`}
                    className={`h-8 text-xs font-medium border ${STATUS_STYLES[l.status] || ""}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell className="py-2 hidden md:table-cell">
                {l.status === "da richiamare" ? (
                  <RecallCell value={l.recall_date} onSave={(v) => patch(l.id, { recall_date: v })} testid={`lead-recall-${l.id}`} />
                ) : (
                  <span className="text-xs text-muted-foreground/40">—</span>
                )}
              </TableCell>
              <TableCell className="py-1 hidden md:table-cell">
                <EditableCell value={l.notes} placeholder="Note..."
                  testid={`lead-notes-${l.id}`}
                  onSave={(v) => patch(l.id, { notes: v })} />
              </TableCell>
              <TableCell className="py-2">
                <div className="flex items-center gap-1.5">
                  <NewContactButton lead={l} onLogged={(id, data) => onChanged && onChanged(id, data)} />
                  <button onClick={() => setEditLead(l)} data-testid={`edit-lead-${l.id}`}
                    title="Modifica contatti azienda"
                    className="text-muted-foreground hover:text-primary transition-colors">
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => setDetailLead(l)} data-testid={`open-detail-${l.id}`}
                    title="Scheda azienda e storico attività"
                    className="text-muted-foreground hover:text-primary transition-colors">
                    <History className="h-3.5 w-3.5" />
                  </button>
                  <a
                    href={l.website
                      ? (l.website.startsWith("http") ? l.website : `https://${l.website}`)
                      : `https://www.google.com/search?q=${encodeURIComponent(`${l.company_name} ${l.comune || ""}`)}`}
                    target="_blank" rel="noopener noreferrer"
                    data-testid={`open-site-${l.id}`}
                    title={l.website ? "Apri sito web (per copiare i contatti)" : "Cerca su Google"}
                    className="text-muted-foreground hover:text-primary transition-colors">
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                  {l.website && (!l.email || !l.phone) && (
                    <button onClick={() => enrich(l)} disabled={enriching === l.id}
                      data-testid={`enrich-lead-${l.id}`} title="Trova email/telefono dal sito"
                      className="text-muted-foreground hover:text-primary transition-colors disabled:opacity-40">
                      <Search className={`h-3.5 w-3.5 ${enriching === l.id ? "animate-pulse" : ""}`} />
                    </button>
                  )}
                  <button onClick={() => patch(l.id, { closed: !l.closed })} data-testid={`toggle-closed-${l.id}`}
                    title={l.closed ? "Riattiva (non cessata)" : "Segna come cessata"}
                    className={`transition-colors ${l.closed ? "text-red-500" : "text-muted-foreground hover:text-red-500"}`}>
                    <Store className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => setLeadToDelete(l)} data-testid={`delete-lead-${l.id}`}
                    className="text-muted-foreground hover:text-red-500 transition-colors">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <AlertDialog open={!!leadToDelete} onOpenChange={(o) => !o && setLeadToDelete(null)}>        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questo nominativo?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare <b>{leadToDelete?.company_name}</b>. L'operazione non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-lead">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-lead"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => { const id = leadToDelete.id; setLeadToDelete(null); remove(id); }}>
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <LeadDetailDialog lead={detailLead} onClose={() => setDetailLead(null)}
        onActivityChanged={(id, data) => onChanged && onChanged(id, data)} />
      <LeadEditDialog lead={editLead} onClose={() => setEditLead(null)}
        onSaved={(id, data) => onChanged && onChanged(id, data)} />
    </div>
  );
}
